import { useState, useEffect, useCallback, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { KBO_TEAMS } from "@/lib/index";
import type { KBOTeam } from "@/lib/index";
import { supabase } from "@/integrations/supabase/client";
import {
  Trophy, Flame, ChevronUp, ChevronDown, Minus,
  Wifi, WifiOff, Calendar, Clock,
} from "lucide-react";

// ── Types ─────────────────────────────────────────────────────────
interface HallRecord {
  id: number;
  date: string;
  rank: number;
  team_id: string;
  count: number;
}

// ── Helpers ───────────────────────────────────────────────────────
function fmtNum(n: number): string {
  return n.toLocaleString("ko-KR");
}

function fmtDate(dateStr: string): string {
  const [y, m, d] = dateStr.split("-");
  return `${y}년 ${Number(m)}월 ${Number(d)}일`;
}

async function fetchVotesFromDB(): Promise<Record<string, number> | null> {
  try {
    const { data, error } = await supabase.from("kbo_votes").select("team_id, count");
    if (error || !data) return null;
    const map: Record<string, number> = {};
    data.forEach((row) => { map[row.team_id] = Number(row.count); });
    return map;
  } catch {
    return null;
  }
}

// ── Team Logo ─────────────────────────────────────────────────────
function TeamLogo({ team, onError }: { team: KBOTeam; onError: () => void }) {
  return (
    <img
      src={team.logoUrl}
      alt={team.name}
      className="w-20 h-20 md:w-24 md:h-24 object-contain drop-shadow-lg"
      onError={onError}
    />
  );
}

function EmojiFallbackLogo({ team }: { team: KBOTeam }) {
  return (
    <div
      className="w-20 h-20 md:w-24 md:h-24 rounded-full flex items-center justify-center text-4xl md:text-5xl"
      style={{ background: `${team.primaryColor}22`, border: `2px solid ${team.primaryColor}55` }}
    >
      {team.emoji}
    </div>
  );
}

// ── Floating +1 ───────────────────────────────────────────────────
interface Particle { id: number; x: number; y: number; }

function FloatingPlus({ particle, color }: { particle: Particle; color: string }) {
  return (
    <motion.div
      key={particle.id}
      initial={{ opacity: 1, y: 0, scale: 1 }}
      animate={{ opacity: 0, y: -60, scale: 1.4 }}
      transition={{ duration: 0.8, ease: "easeOut" }}
      style={{
        position: "absolute", left: particle.x, top: particle.y,
        color, fontWeight: 900, fontSize: "1.5rem",
        pointerEvents: "none", zIndex: 50,
        textShadow: `0 0 10px ${color}`,
      }}
    >
      +1
    </motion.div>
  );
}

// ── Team Card ─────────────────────────────────────────────────────
interface TeamCardProps {
  team: KBOTeam; clicks: number; rank: number;
  prevRank: number; totalClicks: number;
  onVote: (id: string, e: React.MouseEvent) => void;
}

function TeamCard({ team, clicks, rank, prevRank, totalClicks, onVote }: TeamCardProps) {
  const [particles, setParticles] = useState<Particle[]>([]);
  const [isClicked, setIsClicked] = useState(false);
  const [imgError, setImgError] = useState(false);

  const percentage = totalClicks > 0 ? ((clicks / totalClicks) * 100).toFixed(1) : "0.0";
  const rankDiff = prevRank - rank;
  const RankIcon = rankDiff > 0 ? ChevronUp : rankDiff < 0 ? ChevronDown : Minus;
  const rankColor = rankDiff > 0 ? "#22c55e" : rankDiff < 0 ? "#ef4444" : "#888";

  const handleClick = (e: React.MouseEvent) => {
    onVote(team.id, e);
    setIsClicked(true);
    setTimeout(() => setIsClicked(false), 150);
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    const id = Date.now() + Math.random();
    setParticles((p) => [...p, { id, x: e.clientX - rect.left - 10, y: e.clientY - rect.top - 20 }]);
    setTimeout(() => setParticles((p) => p.filter((x) => x.id !== id)), 900);
  };

  const rankBadge =
    rank === 1 ? { background: "linear-gradient(135deg,#FFD700,#FF8C00)", color: "#000" }
    : rank === 2 ? { background: "linear-gradient(135deg,#C0C0C0,#888)", color: "#000" }
    : rank === 3 ? { background: "linear-gradient(135deg,#CD7F32,#8B4513)", color: "#fff" }
    : { background: "rgba(255,255,255,0.1)", color: "#aaa" };

  return (
    <motion.div
      layout
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ type: "spring", stiffness: 300, damping: 30 }}
      className="relative cursor-pointer select-none"
      onClick={handleClick}
      whileTap={{ scale: 0.95 }}
    >
      <motion.div
        animate={isClicked ? { opacity: 0.8, scale: 1.05 } : { opacity: 0, scale: 1 }}
        transition={{ duration: 0.15 }}
        className="absolute inset-0 pointer-events-none"
        style={{ boxShadow: `0 0 40px ${team.glowColor}`, borderRadius: "1rem" }}
      />
      <div
        className="relative rounded-2xl p-4 md:p-5 flex flex-col items-center gap-3 overflow-hidden"
        style={{
          background: `linear-gradient(145deg,${team.primaryColor}18 0%,rgba(15,15,25,0.95) 60%)`,
          border: `1.5px solid ${team.primaryColor}44`,
          boxShadow: `0 4px 24px ${team.glowColor}30`,
          transition: "border-color .2s,box-shadow .2s",
        }}
        onMouseEnter={(e) => {
          (e.currentTarget as HTMLDivElement).style.borderColor = `${team.primaryColor}99`;
          (e.currentTarget as HTMLDivElement).style.boxShadow = `0 8px 32px ${team.glowColor}55`;
        }}
        onMouseLeave={(e) => {
          (e.currentTarget as HTMLDivElement).style.borderColor = `${team.primaryColor}44`;
          (e.currentTarget as HTMLDivElement).style.boxShadow = `0 4px 24px ${team.glowColor}30`;
        }}
      >
        <div className="absolute top-3 left-3 w-7 h-7 rounded-full flex items-center justify-center text-xs font-black" style={rankBadge}>
          {rank}
        </div>
        <div className="absolute top-3 right-3 flex items-center gap-0.5">
          <RankIcon size={14} style={{ color: rankColor }} />
          {rankDiff !== 0 && <span className="text-xs font-bold" style={{ color: rankColor }}>{Math.abs(rankDiff)}</span>}
        </div>
        <div className="relative mt-2">
          {imgError ? <EmojiFallbackLogo team={team} /> : <TeamLogo team={team} onError={() => setImgError(true)} />}
        </div>
        <div className="text-center">
          <p className="text-sm font-bold text-white leading-tight">{team.name}</p>
          <p className="text-xs text-white/40 mt-0.5">{team.city}</p>
        </div>
        <div className="text-center">
          <p className="text-2xl md:text-3xl font-black font-mono" style={{ color: team.displayColor }}>
            {fmtNum(clicks)}
          </p>
          <p className="text-xs text-white/40">응원 수</p>
        </div>
        <div className="w-full">
          <div className="flex justify-between text-xs text-white/40 mb-1">
            <span>점유율</span><span>{percentage}%</span>
          </div>
          <div className="w-full h-1.5 rounded-full bg-white/10 overflow-hidden">
            <motion.div
              className="h-full rounded-full"
              style={{ background: team.displayColor }}
              initial={{ width: 0 }}
              animate={{ width: `${percentage}%` }}
              transition={{ type: "spring", stiffness: 120, damping: 20 }}
            />
          </div>
        </div>
        <p className="text-xs text-white/25 mt-1">탭하여 응원!</p>
      </div>
      {particles.map((p) => <FloatingPlus key={p.id} particle={p} color={team.displayColor} />)}
    </motion.div>
  );
}

// ── Ticker ────────────────────────────────────────────────────────
function TickerBar({ rankedTeams, votes }: { rankedTeams: KBOTeam[]; votes: Record<string, number> }) {
  return (
    <div className="overflow-hidden whitespace-nowrap bg-white/5 border-y border-white/10 py-2">
      <motion.div
        animate={{ x: ["0%", "-50%"] }}
        transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
        className="inline-flex gap-6"
      >
        {[...rankedTeams, ...rankedTeams].map((team, i) => (
          <span key={`${team.id}-${i}`} className="inline-flex items-center gap-1.5 text-sm font-bold">
            <span>{team.emoji}</span>
            <span style={{ color: team.displayColor }}>{team.shortName}</span>
            <span className="text-white/60 font-mono">{fmtNum(votes[team.id] ?? 0)}</span>
          </span>
        ))}
      </motion.div>
    </div>
  );
}

// ── Hall of Fame Section ──────────────────────────────────────────
const MEDALS = ["🥇", "🥈", "🥉"];
const MEDAL_BG = [
  "linear-gradient(135deg,rgba(255,215,0,.15),rgba(255,140,0,.08))",
  "linear-gradient(135deg,rgba(192,192,192,.12),rgba(136,136,136,.06))",
  "linear-gradient(135deg,rgba(205,127,50,.12),rgba(139,69,19,.06))",
];
const MEDAL_BORDER = ["rgba(255,215,0,.35)", "rgba(192,192,192,.25)", "rgba(205,127,50,.25)"];

function HallOfFameSection() {
  const [records, setRecords] = useState<HallRecord[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    supabase
      .from("kbo_hall_of_fame")
      .select("*")
      .order("date", { ascending: false })
      .order("rank", { ascending: true })
      .then(({ data }) => {
        if (data) setRecords(data as HallRecord[]);
        setLoading(false);
      });
  }, []);

  // 날짜별 그룹핑
  const grouped: Record<string, HallRecord[]> = {};
  records.forEach((r) => {
    if (!grouped[r.date]) grouped[r.date] = [];
    grouped[r.date].push(r);
  });
  const dates = Object.keys(grouped).sort((a, b) => b.localeCompare(a));

  if (loading) {
    return (
      <div className="text-center py-20 text-white/40">
        <p className="text-3xl mb-3 animate-bounce">🏆</p>
        <p className="text-sm">불러오는 중...</p>
      </div>
    );
  }

  if (dates.length === 0) {
    return (
      <div className="text-center py-24 px-4">
        <p className="text-7xl mb-5">🏆</p>
        <p className="text-white/60 font-bold text-lg">아직 기록이 없습니다</p>
        <p className="text-white/30 text-sm mt-2 leading-relaxed">
          매일 자정(00:00)에 그날의 1·2·3위가<br />여기에 영구 기록됩니다!
        </p>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto px-4 pb-16">
      <div className="space-y-4">
        {dates.map((date) => {
          const dayRecords = grouped[date];
          return (
            <motion.div
              key={date}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ type: "spring", stiffness: 200, damping: 25 }}
              className="rounded-2xl overflow-hidden"
              style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.09)" }}
            >
              {/* 날짜 헤더 */}
              <div className="px-4 py-3 flex items-center gap-2" style={{ borderBottom: "1px solid rgba(255,255,255,0.07)" }}>
                <Calendar size={13} className="text-white/40" />
                <span className="text-sm font-bold text-white/70">{fmtDate(date)}</span>
                <span className="ml-auto text-xs text-white/25">일일 최종 기록</span>
              </div>
              {/* 순위 목록 — Top 3 */}
              {dayRecords
                .filter((r) => r.rank <= 3)
                .sort((a, b) => a.rank - b.rank)
                .map((record) => {
                  const team = KBO_TEAMS.find((t) => t.id === record.team_id);
                  if (!team) return null;
                  const ri = record.rank - 1;
                  return (
                    <div
                      key={record.rank}
                      className="flex items-center gap-3 px-4 py-3.5"
                      style={{
                        background: MEDAL_BG[ri],
                        borderBottom: "1px solid rgba(255,255,255,0.05)",
                      }}
                    >
                      <span className="text-2xl w-8 text-center flex-shrink-0">{MEDALS[ri]}</span>
                      <div
                        className="w-11 h-11 rounded-full flex items-center justify-center flex-shrink-0"
                        style={{ background: `${team.primaryColor}22`, border: `1.5px solid ${MEDAL_BORDER[ri]}` }}
                      >
                        <img
                          src={team.logoUrl} alt={team.name}
                          className="w-8 h-8 object-contain"
                          onError={(e) => {
                            const el = e.currentTarget as HTMLImageElement;
                            el.style.display = "none";
                            if (el.parentElement) el.parentElement.innerHTML = `<span style="font-size:1.3rem">${team.emoji}</span>`;
                          }}
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-bold text-white truncate">{team.name}</p>
                        <p className="text-xs text-white/35">{team.city}</p>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <p className="font-black font-mono text-lg leading-none" style={{ color: team.displayColor }}>
                          {fmtNum(record.count)}
                        </p>
                        <p className="text-xs text-white/30 mt-0.5">표</p>
                      </div>
                    </div>
                  );
                })}

              {/* 꼴찌 구분선 */}
              {dayRecords.some((r) => r.rank === 10) && (
                <div className="flex items-center gap-2 px-4 py-1.5" style={{ background: "rgba(0,0,0,0.2)" }}>
                  <div className="flex-1 h-px bg-white/10" />
                  <span className="text-xs text-white/25 font-bold tracking-widest">오늘의 꼴찌</span>
                  <div className="flex-1 h-px bg-white/10" />
                </div>
              )}

              {/* 꼴찌 (rank 10) */}
              {dayRecords
                .filter((r) => r.rank === 10)
                .map((record) => {
                  const team = KBO_TEAMS.find((t) => t.id === record.team_id);
                  if (!team) return null;
                  return (
                    <div
                      key="last"
                      className="flex items-center gap-3 px-4 py-3.5"
                      style={{ background: "rgba(255,255,255,0.02)" }}
                    >
                      <span className="text-2xl w-8 text-center flex-shrink-0">🪣</span>
                      <div
                        className="w-11 h-11 rounded-full flex items-center justify-center flex-shrink-0"
                        style={{ background: "rgba(255,255,255,0.05)", border: "1.5px solid rgba(255,255,255,0.12)" }}
                      >
                        <img
                          src={team.logoUrl} alt={team.name}
                          className="w-8 h-8 object-contain opacity-60"
                          onError={(e) => {
                            const el = e.currentTarget as HTMLImageElement;
                            el.style.display = "none";
                            if (el.parentElement) el.parentElement.innerHTML = `<span style="font-size:1.3rem;opacity:.5">${team.emoji}</span>`;
                          }}
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-bold text-white/50 truncate">{team.name}</p>
                        <p className="text-xs text-white/25">{team.city} · 10위</p>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <p className="font-black font-mono text-lg leading-none text-white/40">
                          {fmtNum(record.count)}
                        </p>
                        <p className="text-xs text-white/20 mt-0.5">표</p>
                      </div>
                    </div>
                  );
                })}
            </motion.div>
          );
        })}
      </div>
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────
export default function Home() {
  const [votes, setVotes] = useState<Record<string, number>>(
    () => Object.fromEntries(KBO_TEAMS.map((t) => [t.id, 0]))
  );
  const [prevRanks, setPrevRanks] = useState<Record<string, number>>({});
  const [isConnected, setIsConnected] = useState(false);
  const [isPolling, setIsPolling] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<"vote" | "hall">("vote");
  const [timeLeft, setTimeLeft] = useState("");
  const channelRef = useRef<ReturnType<typeof supabase.channel> | null>(null);
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // 초기화까지 카운트다운 (KST 자정 기준)
  useEffect(() => {
    const update = () => {
      const now = new Date();
      const kstMs = now.getTime() + 9 * 60 * 60 * 1000;
      const msSinceMidnight = kstMs % (24 * 60 * 60 * 1000);
      const msUntilMidnight = 24 * 60 * 60 * 1000 - msSinceMidnight;
      const total = Math.floor(msUntilMidnight / 1000);
      const h = Math.floor(total / 3600);
      const m = Math.floor((total % 3600) / 60);
      const s = total % 60;
      setTimeLeft(
        `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`
      );
    };
    update();
    const t = setInterval(update, 1000);
    return () => clearInterval(t);
  }, []);

  // 초기 데이터 로드
  useEffect(() => {
    fetchVotesFromDB().then((map) => {
      if (map) setVotes((prev) => ({ ...prev, ...map }));
      setIsLoading(false);
    });
  }, []);

  // Realtime 구독 + 폴링 폴백
  useEffect(() => {
    let realtimeWorking = false;
    try {
      const channel = supabase
        .channel("kbo_votes_realtime")
        .on("postgres_changes", { event: "UPDATE", schema: "public", table: "kbo_votes" }, (payload) => {
          realtimeWorking = true;
          if (pollingRef.current) { clearInterval(pollingRef.current); pollingRef.current = null; }
          const { team_id, count } = payload.new as { team_id: string; count: number };
          setVotes((prev) => {
            const sorted = [...KBO_TEAMS].sort((a, b) => (prev[b.id] ?? 0) - (prev[a.id] ?? 0));
            setPrevRanks(Object.fromEntries(sorted.map((t, i) => [t.id, i + 1])));
            return { ...prev, [team_id]: Number(count) };
          });
        })
        .subscribe((status) => {
          const connected = status === "SUBSCRIBED";
          setIsConnected(connected);
          if (!connected && !realtimeWorking && !pollingRef.current) {
            setIsPolling(true);
            pollingRef.current = setInterval(async () => {
              const map = await fetchVotesFromDB();
              if (map) setVotes((prev) => ({ ...prev, ...map }));
            }, 5000);
          }
        });
      channelRef.current = channel;
    } catch {
      setIsPolling(true);
      pollingRef.current = setInterval(async () => {
        const map = await fetchVotesFromDB();
        if (map) setVotes((prev) => ({ ...prev, ...map }));
      }, 5000);
    }
    return () => {
      if (channelRef.current) supabase.removeChannel(channelRef.current);
      if (pollingRef.current) clearInterval(pollingRef.current);
    };
  }, []);

  const getSortedTeams = useCallback(
    () => [...KBO_TEAMS].sort((a, b) => (votes[b.id] ?? 0) - (votes[a.id] ?? 0)),
    [votes]
  );

  const rankedTeams = getSortedTeams();
  const rankMap = Object.fromEntries(rankedTeams.map((t, i) => [t.id, i + 1]));
  const totalClicks = Object.values(votes).reduce((a, b) => a + b, 0);

  const handleVote = useCallback(
    async (teamId: string, _e: React.MouseEvent) => {
      setPrevRanks({ ...rankMap });
      setVotes((prev) => ({ ...prev, [teamId]: (prev[teamId] ?? 0) + 1 }));
      try { await supabase.rpc("increment_vote", { p_team_id: teamId }); } catch { /* ignore */ }
    },
    [rankMap]
  );

  const top3 = rankedTeams.slice(0, 3);

  if (isLoading) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{ background: "radial-gradient(ellipse at 50% 0%,oklch(0.20 0.05 10) 0%,oklch(0.12 0.02 260) 50%,oklch(0.08 0.01 260) 100%)" }}
      >
        <div className="text-center">
          <p className="text-4xl mb-4 animate-bounce">⚾</p>
          <p className="text-white/60 text-sm">데이터 불러오는 중...</p>
        </div>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen text-white"
      style={{ background: "radial-gradient(ellipse at 50% 0%,oklch(0.20 0.05 10) 0%,oklch(0.12 0.02 260) 50%,oklch(0.08 0.01 260) 100%)" }}
    >
      {/* Ticker */}
      <TickerBar rankedTeams={rankedTeams} votes={votes} />

      {/* Header */}
      <header className="text-center py-8 px-4">
        <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} transition={{ type: "spring", stiffness: 200, damping: 20 }}>
          {/* 연결 상태 */}
          <div className="flex items-center justify-center gap-2 mb-4">
            {isConnected ? (
              <>
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" />
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500" />
                </span>
                <span className="text-xs text-green-400 font-bold flex items-center gap-1"><Wifi size={12} /> 실시간 연결됨</span>
              </>
            ) : isPolling ? (
              <>
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-yellow-400 opacity-75" />
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-yellow-500" />
                </span>
                <span className="text-xs text-yellow-400 font-bold flex items-center gap-1"><Wifi size={12} /> 5초마다 자동 갱신 중</span>
              </>
            ) : (
              <span className="text-xs text-white/30 flex items-center gap-1"><WifiOff size={12} /> 연결 중...</span>
            )}
          </div>

          <p className="text-xs font-bold tracking-[0.3em] text-white/40 uppercase mb-3">KBO League 2026</p>
          <h1 className="text-4xl md:text-6xl font-black mb-2 leading-tight">⚾ 내 팀을 응원해!</h1>
          <p className="text-white/50 text-base md:text-lg mt-2">로고를 클릭해서 내 팀에 응원을 보내세요 🎉</p>

          {/* 탭 스위처 */}
          <div className="flex justify-center mt-6">
            <div className="flex bg-white/5 rounded-full p-1 border border-white/10 gap-1">
              <button
                onClick={() => setActiveTab("vote")}
                className="px-5 py-2 rounded-full text-sm font-bold transition-all duration-200"
                style={activeTab === "vote"
                  ? { background: "linear-gradient(135deg,oklch(0.58 0.25 10),oklch(0.45 0.22 10))", color: "#fff", boxShadow: "0 2px 12px rgba(200,32,30,0.4)" }
                  : { color: "rgba(255,255,255,0.45)" }}
              >
                ⚾ 응원 투표
              </button>
              <button
                onClick={() => setActiveTab("hall")}
                className="px-5 py-2 rounded-full text-sm font-bold transition-all duration-200"
                style={activeTab === "hall"
                  ? { background: "linear-gradient(135deg,oklch(0.58 0.25 10),oklch(0.45 0.22 10))", color: "#fff", boxShadow: "0 2px 12px rgba(200,32,30,0.4)" }
                  : { color: "rgba(255,255,255,0.45)" }}
              >
                🏆 명예의 전당
              </button>
            </div>
          </div>

          {/* 통계 바 (투표 탭만) */}
          {activeTab === "vote" && (
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }}
              className="inline-flex flex-wrap justify-center gap-x-6 gap-y-3 mt-6 px-6 py-3 rounded-2xl bg-white/5 border border-white/10 text-sm"
            >
              <div className="text-center">
                <p className="text-white/40 text-xs">총 응원 수</p>
                <p className="text-white font-black font-mono text-lg">{fmtNum(totalClicks)}</p>
              </div>
              <div className="w-px bg-white/10 hidden sm:block" />
              <div className="text-center">
                <p className="text-white/40 text-xs">참여 팀</p>
                <p className="text-white font-black font-mono text-lg">10개</p>
              </div>
              <div className="w-px bg-white/10 hidden sm:block" />
              <div className="text-center">
                <p className="text-white/40 text-xs">1위</p>
                <p className="font-black text-lg" style={{ color: rankedTeams[0]?.displayColor ?? "#fff" }}>
                  {rankedTeams[0]?.shortName ?? "-"}
                </p>
              </div>
              <div className="w-px bg-white/10 hidden sm:block" />
              <div className="text-center">
                <p className="text-white/40 text-xs flex items-center justify-center gap-1"><Clock size={10} /> 초기화까지</p>
                <p className="text-white font-black font-mono text-lg">{timeLeft}</p>
              </div>
            </motion.div>
          )}
        </motion.div>
      </header>

      {/* 탭 콘텐츠 */}
      <AnimatePresence mode="wait">
        {activeTab === "vote" ? (
          <motion.div key="vote" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.2 }}>
            {/* Top 3 Podium */}
            {totalClicks > 0 && (
              <section className="px-4 md:px-8 mb-8">
                <div className="max-w-2xl mx-auto">
                  <div className="flex items-end justify-center gap-3">
                    {top3[1] && (
                      <motion.div layout className="flex-1 rounded-xl p-3 text-center"
                        style={{ background: `linear-gradient(180deg,${top3[1].primaryColor}22 0%,rgba(0,0,0,.3) 100%)`, border: `1px solid ${top3[1].primaryColor}44`, minHeight: "100px" }}>
                        <p className="text-2xl mb-1">🥈</p>
                        <p className="text-xs font-bold text-white/70">{top3[1].shortName}</p>
                        <p className="font-black font-mono text-sm text-white">{fmtNum(votes[top3[1].id])}</p>
                      </motion.div>
                    )}
                    {top3[0] && (
                      <motion.div layout className="flex-1 rounded-xl p-3 text-center"
                        style={{ background: `linear-gradient(180deg,${top3[0].primaryColor}33 0%,rgba(0,0,0,.3) 100%)`, border: `1.5px solid ${top3[0].primaryColor}88`, boxShadow: `0 0 30px ${top3[0].glowColor}`, minHeight: "130px" }}>
                        <p className="text-3xl mb-1">🏆</p>
                        <p className="text-sm font-bold text-white">{top3[0].shortName}</p>
                        <p className="font-black font-mono text-base" style={{ color: top3[0].displayColor }}>{fmtNum(votes[top3[0].id])}</p>
                      </motion.div>
                    )}
                    {top3[2] && (
                      <motion.div layout className="flex-1 rounded-xl p-3 text-center"
                        style={{ background: `linear-gradient(180deg,${top3[2].primaryColor}22 0%,rgba(0,0,0,.3) 100%)`, border: `1px solid ${top3[2].primaryColor}44`, minHeight: "85px" }}>
                        <p className="text-2xl mb-1">🥉</p>
                        <p className="text-xs font-bold text-white/70">{top3[2].shortName}</p>
                        <p className="font-black font-mono text-sm text-white">{fmtNum(votes[top3[2].id])}</p>
                      </motion.div>
                    )}
                  </div>
                </div>
              </section>
            )}

            {/* 팀 그리드 */}
            <main className="px-4 md:px-8 pb-12">
              <div className="max-w-5xl mx-auto">
                <div className="flex items-center mb-4 px-1">
                  <div className="flex items-center gap-2 text-white/50 text-sm">
                    <Trophy size={14} /><span>클릭수 기준 실시간 순위</span>
                  </div>
                </div>
                <motion.div layout className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 md:gap-4">
                  <AnimatePresence>
                    {rankedTeams.map((team) => (
                      <TeamCard
                        key={team.id} team={team}
                        clicks={votes[team.id] ?? 0}
                        rank={rankMap[team.id]}
                        prevRank={prevRanks[team.id] ?? rankMap[team.id]}
                        totalClicks={totalClicks}
                        onVote={handleVote}
                      />
                    ))}
                  </AnimatePresence>
                </motion.div>
              </div>
            </main>
          </motion.div>
        ) : (
          <motion.div key="hall" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.2 }}>
            <HallOfFameSection />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Footer */}
      <footer className="text-center py-6 text-white/20 text-xs border-t border-white/5">
        <p className="flex items-center justify-center gap-2">
          <Flame size={12} />
          KBO 팬 투표 · 매일 자정 초기화 · 전 세계 팬들과 함께 실시간으로 응원하세요!
          <Flame size={12} />
        </p>
      </footer>
    </div>
  );
}
